import { createSelector } from 'reselect'
import completeFiltersTree from 'helpers/filter/completeFiltersTree'
import { generateBreadcrumb } from 'helpers/search/breadcrumb'
import config from 'config'

import filterTypes from 'helpers/enums/filters/filterTypes'
import filterTabs from 'helpers/enums/filters/tabs'
import displayTypes from 'helpers/enums/filters/displayTypes'
import { localize } from 'helpers/translator'
import themeTypes from 'helpers/enums/themes/themeTypes'
import userLoggedSelector from 'redux/selectors/user_extra_data/user_logged_selector'
import featureFlagsTypes from 'helpers/enums/featureFlagsTypes'
import featureFlagsSelectorCreator from 'redux/selectors/feature_flags/feature_flags_selector'
import viewTypes from 'helpers/enums/viewTypes'
import formatLessonTypeFromView from 'helpers/lessonTypeFormatter'
import lessonFilterTypes from 'helpers/enums/filters/lessonFilterTypes'

const featureFlagsSelector = featureFlagsSelectorCreator([
	featureFlagsTypes.VIDEO_LESSON_TYPE,
	featureFlagsTypes.ACTIVITY_BANKS,
])

const getStore = state => state.store
const getUserExtra = state => state.user.extra
const getCurrentView = state => state.ui.currentView

const viewsToFiltersTheme = {
	[viewTypes.TIME_TO_CLIMB_CREATION]: themeTypes.LIGHT,
	[viewTypes.MATCHING_PAIRS_CREATION]: themeTypes.LIGHT,
	[viewTypes.MATCHING_PAIRS_STANDALONE_CREATION]: themeTypes.LIGHT,
	[viewTypes.TIME_TO_CLIMB_STANDALONE_CREATION]: themeTypes.LIGHT,
	[viewTypes.DRAW_IT_CREATION]: themeTypes.LIGHT,
	[viewTypes.DRAW_IT_STANDALONE_CREATION]: themeTypes.LIGHT,
	[viewTypes.MATCHING_PAIRS_CREATION]: themeTypes.LIGHT,
}

const getTheme = (currentView, isUserLogged) => (
	viewsToFiltersTheme[currentView] || (isUserLogged ? themeTypes.DARK : themeTypes.LIGHT)
)

const storeSelector = createSelector(
	[ getStore, getUserExtra, userLoggedSelector, getCurrentView, featureFlagsSelector ],
	(storeData, userExtraData, isUserLogged, currentView, featureFlags ) => {
		const isItemSelected = item => selectedFilters.find( entityUID => item.entityUID === entityUID)

		const findSelectedItems = items => items.filter(isItemSelected)
			.map( item => item.items ? item.items[0] : {displayType: '99'})

		const addCategories = categories => {
			let availableCategories = []
			categories.filter(category =>
				// don't show price category for premium users
				!(category.id === config.categories.price && userExtraData && userExtraData.hasPremiumAyce)
			).map(category => ({
				...category,
				numColumns: [config.categories.price, config.categories.grades].includes(category.id) ? 2 : 1,
			})).forEach(category => {
				availableCategories.push(category)
				if (category.type === filterTypes.STANDARDS_FILTER) {
					const selectedItems = findSelectedItems(category.items || []) // passing an empty array in case sub items are not loaded yet
					availableCategories = [...availableCategories, ...addCategories(selectedItems, [])]
				}
			})
			return availableCategories
		}

		const searchAvailableCategories = categories => [...addCategories(categories)]

		const standards = storeData.currentTab === filterTabs.REGULAR_TAB ? storeData.categories : storeData.standard
		const selectedFilters = completeFiltersTree(storeData.selectedFilters, standards)

		const availableCategories = searchAvailableCategories(standards)

		const breadcrumb = generateBreadcrumb(storeData.confirmedFilters, storeData.currentTab, standards)

		let canSearchFilters = false
		if (storeData.currentTab === filterTabs.REGULAR_TAB) {
			canSearchFilters = true
		} else {
			let numStandardSections = new Set([...storeData.categories.map(c => c.title), ...storeData.standard.map(c => c.title)]).size
			if (userExtraData && userExtraData.hasPremiumAyce) {
				numStandardSections -= 1
			}
			if (availableCategories.length > numStandardSections) {
				canSearchFilters = true
			}
		}

		const regularFilters = (storeData.currentTab === filterTabs.REGULAR_TAB) ? availableCategories : []

		let standardFilters

		if ([
			viewTypes.TIME_TO_CLIMB_CREATION,
			viewTypes.TIME_TO_CLIMB_STANDALONE_CREATION,
			viewTypes.DRAW_IT_CREATION,
			viewTypes.DRAW_IT_STANDALONE_CREATION,
			viewTypes.MATCHING_PAIRS_CREATION,
			viewTypes.MATCHING_PAIRS_STANDALONE_CREATION,
		].includes(currentView)) {
			standardFilters = []
		} else if (storeData.currentTab === filterTabs.REGULAR_TAB) {
			standardFilters = storeData.standard.filter( filter => filter.filterType === filterTypes.STANDARDS_FILTER)
		} else if (storeData.currentTab === filterTabs.STANDARDS_TAB) {
			standardFilters = availableCategories.map(filter => {
				let title = filter.title
				let items = filter.items

				switch (title) {
					case 'Subject':
						title = localize('standards_filters_subjects_title')
						break
					case 'Grades':
						title = localize('standards_filters_grades_title')
						break
					case 'Strands':
						items = filter.items.map(item => ({
							...item,
							text: item.text.length > 120 ? item.text.substring(0, 120) + '...' : item.text,
						}))
						break
				}

				return {
					...filter,
					title,
					items,
				}
			})
			if (!canSearchFilters) {
				standardFilters = standardFilters.filter( filter => filter.filterType === filterTypes.STANDARDS_FILTER)
			}
			standardFilters = [
				...standardFilters.filter(item => item.filterType === filterTypes.STANDARDS_FILTER),
				{
					displayType: displayTypes.BUTTON,
					friendlyText: 'clear-standards',
					title: localize('store_clear_standards'),
				},
				...standardFilters.filter(item => item.filterType === filterTypes.REGULAR_FILTER),
			]
		}

		const lessonTypeSelectedFilter = Object
			.keys(storeData.lessonTypeSelectedFilter)
			.filter(k => storeData.lessonTypeSelectedFilter[k])


		const fixedLessonType = formatLessonTypeFromView(currentView)

		const theme = getTheme(currentView, isUserLogged)

		const canFilterTypes =
			(
				!!featureFlags[featureFlagsTypes.VIDEO_LESSON_TYPE]
				&& !fixedLessonType
				&& storeData.hasAccessToFilterTypes
			)

		const availableLessonFilterTypes =
			[
				lessonFilterTypes.REGULAR,
				lessonFilterTypes.VIDEO,
			]
		if (featureFlags[featureFlagsTypes.ACTIVITY_BANKS]) {
			availableLessonFilterTypes.push(lessonFilterTypes.STANDALONE_ACTIVITIES)
		}

		return {
			filters: availableCategories,
			query: storeData.query,
			currentTab: storeData.currentTab,
			selectedFilters,
			canSearchFilters,
			breadcrumb,
			regularFilters,
			standardFilters,
			theme,
			lessonTypeSelectedFilter,
			canFilterTypes,
			fixedLessonType,
			availableLessonFilterTypes,
		}
	}
)

export default storeSelector
